import React, { memo, useMemo, useState } from "react";
import ReactFlow, { MarkerType } from "reactflow";
import "reactflow/dist/style.css";

import { DiagramNode } from "./nodes/diagram-node";
import { TopManualEdge } from "./edges/top-manual-edge";
import { BottomManualEdge } from "./edges/bottom-manual-edge";
import { RulesBracketEdge } from "./edges/rules-bracket-edge";

// import { useNavigate } from "react-router-dom";

import "./flow-diagram.css";

const nodeTypes = {
  diagramNode: DiagramNode,
};

const edgeTypes = {
  topManual: TopManualEdge,
  bottomManual: BottomManualEdge,
  rulesBracket: RulesBracketEdge,
};

const DEFAULTS = {
  X_GAP: 210,
  Y_GAP: 74,
  NODE_W: 140,
  NODE_H: 44,

  TOP_CORRIDOR_Y: 0,
  BOTTOM_CORRIDOR_PAD: 28,
};

function buildManual({ nodes, edges, grid, setHoveredNodeId }) {
  const rfNodes = nodes.map((n) => {
    const g = grid?.[n.id] ?? { x: 0, y: 0 };

    return {
      id: n.id,
      type: "diagramNode",
      position: {
        x: g.x * DEFAULTS.X_GAP,
        y: g.y * DEFAULTS.Y_GAP,
      },
      data: {
        label: n.text,
        gridX: g.x,
        gridY: g.y,
        href: n.href,
        icon: n.icon,
        nodeId: n.id,
        onHover: setHoveredNodeId,
      },
      style: { width: DEFAULTS.NODE_W, height: DEFAULTS.NODE_H },
      draggable: false,
      selectable: false,
    };
  });

  let maxY = 0;
  for (const n of rfNodes) {
    maxY = Math.max(maxY, n.position.y);
  }
  const bottomCorridorY = maxY + DEFAULTS.BOTTOM_CORRIDOR_PAD;

  const rfEdges = edges.map((e, idx) => {
    const route = e.route;

    return {
      id: `e-${e.from}-${e.to}-${idx}`,
      source: e.from,
      target: e.to,
      type:
        e.kind === "rulesBracket"
          ? "rulesBracket"
          : route === "top"
          ? "topManual"
          : route === "bottom"
          ? "bottomManual"
          : "smoothstep",
      style: { strokeWidth: 2 },
      selectable: false,
      data: {
        route,
        topCorridorY: DEFAULTS.TOP_CORRIDOR_Y,
        bottomCorridorY,
        xGap: DEFAULTS.X_GAP,
        nodeH: DEFAULTS.NODE_H,
        pointsTo: e.pointsTo, // "rules"
      },
      markerEnd: { type: MarkerType.Arrow }
    };
  });

  return { rfNodes, rfEdges };
}

export const FlowDiagram = memo(function FlowDiagram({ nodes, edges, grid }) {

  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  const { rfNodes, rfEdges } = useMemo(() => {
    return buildManual({ nodes, edges, grid, setHoveredNodeId });
  }, [nodes, edges, grid]);

  const enhancedEdges = useMemo(() => {
    const baseColor = "#E0E0E9";
    const hoverColor = "#C1C1D2";

    const decorated = rfEdges.map((e, idx) => {
      const isBracket = e.type === "rulesBracket";

      const active =
        isBracket ||
        (!!hoveredNodeId && (e.source === hoveredNodeId || e.target === hoveredNodeId));

      const color = active ? hoverColor : baseColor;

      return {
        ...e,
        data: {
          ...(e.data || {}),
          __idx: idx,
          __active: active,
        },
        style: {
          ...(e.style || {}),
          stroke: color,
          strokeWidth: active ? 2.0 : 1.5,
        },
        markerEnd: {
          type: MarkerType.Arrow,
          color,
        },
      };
    });

    const inactive = [];
    const active = [];
    for (const e of decorated) {
      if (e.data && e.data.__active) active.push(e);
      else inactive.push(e);
    }
    return inactive.concat(active);
  }, [rfEdges, hoveredNodeId]);

  return (
    <div className="flow-diagram">
      <ReactFlow
        nodes={rfNodes}
        edges={enhancedEdges} 

        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.18 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        onNodeClick={(e, node) => {
          const href = node?.data?.href;
          if (!href) return;

          e.preventDefault();
          e.stopPropagation();
          window.open(href, "_blank", "noopener,noreferrer");
        }}
      >
        {/*<Background />*/}
      </ReactFlow>
    </div>
  );
});